<template>
  <b-modal
    id="create-barcode-range-modal"
    v-model="isOpen"
    :title="$t('Generate Barcode Range')"
    size="lg"
    no-close-on-backdrop
    hide-footer
    @show="onShow"
    @hidden="onHidden"
  >
    <!-- Top-of-modal overlap alert (surfaced across both steps) -->
    <b-alert v-model="showOverlapError" variant="danger" dismissible class="mb-1">
      <feather-icon icon="AlertTriangleIcon" size="16" class="mr-50" />
      {{ overlapError }}
    </b-alert>

    <!-- STEP 1 — Form -->
    <validation-observer v-show="step === steps.form" ref="form">
      <b-form @submit.prevent="goToReview">
        <!-- Customer (admin only) -->
        <b-form-group v-if="isAdmin">
          <label class="cbr-label"> {{ $t('Customer') }} <span class="text-danger">*</span> </label>
          <validation-provider #default="{ errors }" :name="$t('Customer')" rules="required">
            <v-select
              v-model="form.customer_id"
              :options="customerOptions"
              :reduce="opt => opt.value"
              :placeholder="$t('Select Customer')"
              :class="{ 'is-invalid': errors.length > 0 }"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <!-- Provider — custom 2-card selector acting as a radiogroup -->
        <b-form-group>
          <label class="cbr-label" :id="providerLabelId">
            {{ $t('Provider') }} <span class="text-danger">*</span>
          </label>
          <div class="cbr-provider-grid" role="radiogroup" :aria-labelledby="providerLabelId">
            <button
              v-for="(p, idx) in providerOptions"
              :key="p.value"
              ref="providerCard"
              type="button"
              role="radio"
              :aria-checked="form.provider_id === p.value"
              :tabindex="providerTabindex(idx)"
              class="cbr-provider-card"
              :class="{
                'cbr-provider-card--active': form.provider_id === p.value,
                'cbr-provider-card--invalid': providerMissing
              }"
              @click="selectProvider(p)"
              @keydown.right.prevent="focusProviderCard(idx + 1)"
              @keydown.down.prevent="focusProviderCard(idx + 1)"
              @keydown.left.prevent="focusProviderCard(idx - 1)"
              @keydown.up.prevent="focusProviderCard(idx - 1)"
              @keydown.space.prevent="selectProvider(p)"
              @keydown.enter.prevent="selectProvider(p)"
            >
              <div class="cbr-provider-card__head">
                <img :src="providerCardLogo(p)" :alt="p.label" class="cbr-provider-card__logo" />
                <span class="cbr-provider-card__name">{{ p.label }}</span>
                <feather-icon
                  v-if="form.provider_id === p.value"
                  icon="CheckCircleIcon"
                  size="16"
                  class="cbr-provider-card__check"
                />
              </div>
              <div class="cbr-provider-card__hint">{{ providerHint(p) }}</div>
            </button>
          </div>
          <small v-if="providerMissing" class="text-danger d-block mt-50">
            {{ $t('Please select a provider') }}
          </small>
        </b-form-group>

        <!-- Start / End barcode -->
        <b-row>
          <b-col sm="6">
            <b-form-group>
              <label class="cbr-label">
                {{ $t('Start Barcode') }} <span class="text-danger">*</span>
              </label>
              <validation-provider
                #default="{ errors }"
                :name="$t('Start Barcode')"
                :rules="{ required: true, regex: barcodeRegexStr }"
                :custom-messages="{ regex: $t('Invalid Barcode Format') }"
              >
                <b-form-input
                  v-model="form.start_barcode"
                  :placeholder="startPlaceholder"
                  :state="errors.length > 0 ? false : null"
                  autocomplete="off"
                  class="cbr-barcode-input"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col sm="6">
            <b-form-group>
              <label class="cbr-label">
                {{ $t('End Barcode') }} <span class="text-danger">*</span>
              </label>
              <validation-provider
                #default="{ errors }"
                :name="$t('End Barcode')"
                :rules="{ required: true, regex: barcodeRegexStr }"
                :custom-messages="{ regex: $t('Invalid Barcode Format') }"
              >
                <b-form-input
                  v-model="form.end_barcode"
                  :placeholder="endPlaceholder"
                  :state="errors.length > 0 ? false : null"
                  autocomplete="off"
                  class="cbr-barcode-input"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Format hint + live feedback -->
        <div class="cbr-feedback">
          <div class="cbr-feedback__hint">
            <feather-icon icon="InfoIcon" size="14" class="mr-50 text-muted" />
            <span class="text-muted">{{ formatHint }}</span>
          </div>

          <b-alert
            v-if="bothBarcodesEntered && prefixMismatch"
            show
            variant="warning"
            class="mt-1 mb-0"
          >
            {{ $t('Prefix Mismatch Error') }}
          </b-alert>
          <b-alert
            v-else-if="bothBarcodesEntered && inverted"
            show
            variant="warning"
            class="mt-1 mb-0"
          >
            {{ $t('Range Inversion Error') }}
          </b-alert>
          <b-alert v-else-if="exceedsMax" show variant="danger" class="mt-1 mb-0">
            {{ $t('Max Range Size Error', { max: formatNumber(maxRangeSize) }) }}
          </b-alert>
          <div v-else-if="rangeSize > 0" class="cbr-count">
            <div class="cbr-count__left">
              <feather-icon icon="PackageIcon" size="16" class="text-primary" />
              <span class="cbr-count__label">{{ $t('Will generate') }}:</span>
              <strong class="cbr-count__value">{{ formatNumber(rangeSize) }}</strong>
              <span class="text-muted">{{ $t('barcodes') }}</span>
            </div>
            <b-badge v-if="inferredType" variant="light-info" pill class="cbr-count__badge">
              <feather-icon icon="TagIcon" size="11" class="mr-25" />
              {{ inferredType === 'small' ? $t('Small Package') : $t('Large Package') }}
            </b-badge>
          </div>
        </div>

        <!-- Footer — step 1 -->
        <div class="cbr-actions">
          <b-button variant="outline-secondary" @click="isOpen = false">
            {{ $t('Cancel') }}
          </b-button>
          <b-button variant="primary" type="submit">
            {{ $t('Continue') }}
            <feather-icon icon="ArrowRightIcon" size="14" class="ml-50" />
          </b-button>
        </div>
      </b-form>
    </validation-observer>

    <!-- STEP 2 — Review -->
    <div v-show="step === steps.review" class="cbr-review">
      <button type="button" class="cbr-review__back" @click="step = steps.form">
        <feather-icon icon="ArrowLeftIcon" size="14" class="mr-25" />
        {{ $t('Edit') }}
      </button>

      <h4 class="cbr-review__title">
        <feather-icon icon="AlertCircleIcon" size="18" class="mr-50 text-warning" />
        {{ $t("You're about to create") }}
      </h4>

      <div class="cbr-review__card">
        <div class="cbr-review__row">
          <span class="cbr-review__label">{{ $t('Customer') }}</span>
          <span class="cbr-review__value">
            {{ selectedCustomerName || '—' }}
          </span>
        </div>
        <div class="cbr-review__row">
          <span class="cbr-review__label">{{ $t('Provider') }}</span>
          <span class="cbr-review__value">
            {{ selectedProvider && selectedProvider.label }}
          </span>
        </div>
        <div class="cbr-review__row">
          <span class="cbr-review__label">{{ $t('Barcode Range') }}</span>
          <span class="cbr-review__value cbr-review__value--mono">
            {{ form.start_barcode }}<br />
            <span class="text-muted">→ {{ form.end_barcode }}</span>
          </span>
        </div>
        <div v-if="inferredType" class="cbr-review__row">
          <span class="cbr-review__label">{{ $t('Barcode Type') }}</span>
          <span class="cbr-review__value">
            <b-badge variant="light-info" pill>
              <feather-icon icon="TagIcon" size="11" class="mr-25" />
              {{ inferredType === 'small' ? $t('Small Package') : $t('Large Package') }}
            </b-badge>
          </span>
        </div>
        <div class="cbr-review__total">
          <span class="cbr-review__total-label">
            {{ $t('Will generate') }}
          </span>
          <strong class="cbr-review__total-value">
            {{ formatNumber(rangeSize) }}
            <span class="cbr-review__total-unit">{{ $t('barcodes') }}</span>
          </strong>
        </div>
      </div>

      <div class="cbr-review__warning">
        <feather-icon icon="AlertTriangleIcon" size="14" class="mr-50" />
        {{ $t('This action cannot be undone.') }}
      </div>

      <div class="cbr-actions">
        <b-button variant="outline-secondary" @click="step = steps.form">
          {{ $t('Edit') }}
        </b-button>
        <b-button variant="primary" :disabled="submitting" @click="confirmSubmit">
          <b-spinner v-if="submitting" small class="mr-50" />
          {{ confirmButtonLabel }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BButton,
  BAlert,
  BBadge,
  BSpinner
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { mapGetters } from 'vuex';
import { helper } from '@core/mixins/helper';
import useService from '@/auth/service/useService';
import { getProviderLogo } from '@/libs/providerHelper';
import barcodePoolFormatters from '@/views/pages/barcode-pool/barcodePoolFormatters.mixin';
import {
  BARCODE_REGEX,
  BIG_RANGE_THRESHOLD,
  MAX_RANGE_SIZE,
  calculateRangeSize,
  getBarcodeNumericPart,
  inferBarcodeType
} from '@/constants/barcodePool';

const STEP_FORM = 'form';
const STEP_REVIEW = 'review';

let instanceCounter = 0;

const DIGITS_PATTERN = /^\d{12}$/;
const ABROAD_PATTERN = /^([A-Z]{2})\d{9}TR$/;

function barcodeFamily(barcode) {
  if (!barcode) return null;
  if (DIGITS_PATTERN.test(barcode)) return { kind: 'digits' };
  const m = barcode.match(ABROAD_PATTERN);
  return m ? { kind: 'abroad', prefix: m[1] } : null;
}

const emptyForm = () => ({
  customer_id: null,
  provider_id: null,
  start_barcode: '',
  end_barcode: ''
});

export default {
  name: 'CreateBarcodeRangeModal',

  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BButton,
    BAlert,
    BBadge,
    BSpinner,
    ValidationObserver,
    ValidationProvider,
    vSelect
  },

  mixins: [helper, barcodePoolFormatters],

  props: {
    providerOptions: {
      type: Array,
      default: () => []
    },
    customerOptions: {
      type: Array,
      default: () => []
    }
  },

  data() {
    instanceCounter += 1;
    return {
      isOpen: false,
      step: STEP_FORM,
      submitting: false,
      hasAttemptedSubmit: false,
      form: emptyForm(),
      overlapError: '',
      instanceId: instanceCounter
    };
  },

  computed: {
    ...mapGetters('auth', ['isAdmin']),

    barcodeRegexStr: () => BARCODE_REGEX.source,
    maxRangeSize: () => MAX_RANGE_SIZE,
    steps: () => ({ form: STEP_FORM, review: STEP_REVIEW }),

    showOverlapError: {
      get() {
        return !!this.overlapError;
      },
      set(val) {
        if (!val) this.overlapError = '';
      }
    },

    providerLabelId() {
      return `cbr-provider-label-${this.instanceId}`;
    },

    providerMissing() {
      return this.hasAttemptedSubmit && !this.form.provider_id;
    },

    selectedProvider() {
      return this.providerOptions.find(p => p.value === this.form.provider_id);
    },

    selectedCustomerName() {
      if (!this.isAdmin) return '';
      const c = this.customerOptions.find(opt => opt.value === this.form.customer_id);
      return c ? c.label : '';
    },

    isPttAbroad() {
      return this.selectedProvider?.code === 'PttAbroad';
    },

    startPlaceholder() {
      return this.isPttAbroad ? 'LE999300000TR' : '275373000000';
    },

    endPlaceholder() {
      return this.isPttAbroad ? 'LE999399999TR' : '275373099999';
    },

    formatHint() {
      if (!this.selectedProvider) {
        return this.$t('Pick a provider to see format');
      }
      if (this.isPttAbroad) {
        return this.$t(
          'Format: 2 letters + 9 digits + TR (LE = Small, CE = Large). Max {max} barcodes.',
          { max: this.formatNumber(MAX_RANGE_SIZE) }
        );
      }
      return this.$t('Format: 12 digits. Max {max} barcodes.', {
        max: this.formatNumber(MAX_RANGE_SIZE)
      });
    },

    inferredType() {
      return inferBarcodeType(this.form.start_barcode);
    },

    bothBarcodesEntered() {
      return !!(this.form.start_barcode && this.form.end_barcode);
    },

    prefixMismatch() {
      if (!this.bothBarcodesEntered) return false;
      const a = barcodeFamily(this.form.start_barcode);
      const b = barcodeFamily(this.form.end_barcode);
      if (!a || !b || a.kind !== b.kind) return true;
      if (a.kind === 'abroad' && a.prefix !== b.prefix) return true;
      return false;
    },

    inverted() {
      const startNum = getBarcodeNumericPart(this.form.start_barcode);
      const endNum = getBarcodeNumericPart(this.form.end_barcode);
      if (!startNum || !endNum) return false;
      return Number(startNum) > Number(endNum);
    },

    rangeSize() {
      return calculateRangeSize(this.form.start_barcode, this.form.end_barcode);
    },

    exceedsMax() {
      return this.rangeSize > MAX_RANGE_SIZE;
    },

    canSubmit() {
      const { provider_id, start_barcode, end_barcode, customer_id } = this.form;
      if (!provider_id || !start_barcode || !end_barcode) return false;
      if (this.isAdmin && !customer_id) return false;
      if (this.prefixMismatch || this.inverted || this.exceedsMax) return false;
      return this.rangeSize > 0;
    },

    confirmButtonLabel() {
      if (this.submitting) return this.$t('Please Wait');
      return this.$t('Generate N Barcodes', {
        count: this.formatNumber(this.rangeSize)
      });
    }
  },

  methods: {
    show() {
      this.isOpen = true;
    },

    onShow() {
      this.resetForm();
    },

    onHidden() {
      this.resetForm();
    },

    resetForm() {
      this.form = emptyForm();
      this.overlapError = '';
      this.submitting = false;
      this.hasAttemptedSubmit = false;
      this.step = STEP_FORM;
      if (this.$refs.form) {
        this.$nextTick(() => this.$refs.form.reset());
      }
    },

    providerHint(p) {
      if (p.code === 'PttAbroad') return this.$t('Abroad · LE/CE + TR');
      return this.$t('Domestic · 12 digits');
    },

    providerCardLogo(p) {
      return getProviderLogo({ id: p.value, provider_code: p.code });
    },

    providerTabindex(idx) {
      if (this.form.provider_id) {
        const selectedIdx = this.providerOptions.findIndex(p => p.value === this.form.provider_id);
        return idx === selectedIdx ? 0 : -1;
      }
      return idx === 0 ? 0 : -1;
    },

    selectProvider(p) {
      this.form.provider_id = p.value;
      this.form.start_barcode = '';
      this.form.end_barcode = '';
    },

    focusProviderCard(idx) {
      const cards = this.$refs.providerCard;
      if (!cards || !cards.length) return;
      const len = cards.length;
      const wrapped = ((idx % len) + len) % len;
      cards[wrapped].focus();
    },

    async goToReview() {
      this.hasAttemptedSubmit = true;
      const valid = await this.$refs.form.validate();
      if (!valid || !this.canSubmit) return;
      this.step = STEP_REVIEW;
    },

    async confirmSubmit() {
      if (this.submitting) return;

      if (this.rangeSize >= BIG_RANGE_THRESHOLD) {
        const confirmed = await this.$swal({
          title: this.$t('Large range confirmation title'),
          html: this.$t('Large range confirmation body', {
            count: this.formatNumber(this.rangeSize)
          }),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.$t('Yes, continue'),
          cancelButtonText: this.$t('Cancel'),
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-secondary ml-1'
          },
          buttonsStyling: false
        });
        if (!confirmed.value) return;
      }

      this.overlapError = '';
      this.submitting = true;

      const payload = {
        provider_id: this.form.provider_id,
        start_barcode: this.form.start_barcode,
        end_barcode: this.form.end_barcode
      };
      if (this.isAdmin) payload.customer_id = this.form.customer_id;

      const apiCall = this.isAdmin
        ? useService.barcodePoolService.generateBarcodePoolAdmin(payload)
        : useService.barcodePoolService.generateBarcodePoolCustomer(payload);

      apiCall
        .then(response => {
          this.showSuccessMessage(response.message || this.$t('Successful'));
          this.isOpen = false;
          this.$emit('created', response.data || null);
        })
        .catch(messages => {
          this.handleSubmitError(messages);
        })
        .finally(() => {
          this.submitting = false;
        });
    },

    handleSubmitError(messages) {
      if (!Array.isArray(messages) || !messages.length) return;
      const overlap = messages.find(m => /overlap/i.test(m.message || ''));
      if (overlap) {
        this.overlapError = overlap.message;
        this.step = STEP_FORM;
        return;
      }
      this.showBackendErrors(messages);
    }
  }
};
</script>

<style lang="scss">
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';
@import '@core/scss/vue/libs/vue-select.scss';

#create-barcode-range-modal {
  .cbr-label {
    font-size: 0.85rem;
    font-weight: 500;
    color: $body-color;
    margin-bottom: 0.4rem;
    display: block;

    .dark-layout & {
      color: $theme-dark-body-color;
    }

    .text-danger {
      margin-left: 0.15rem;
    }
  }

  .cbr-provider-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  .cbr-provider-card {
    background: $white;
    border: 1px solid $border-color;
    border-radius: 0.75rem;
    padding: 1rem 1.25rem;
    text-align: left;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;

    &:hover {
      border-color: rgba($primary, 0.5);
      background: rgba($primary, 0.02);
      transform: translateY(-2px);
      box-shadow: 0 4px 15px 0 rgba(34, 41, 47, 0.05);
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba($primary, 0.15);
    }

    &__head {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      margin-bottom: 0.4rem;
    }

    &__logo {
      width: 36px;
      height: 36px;
      object-fit: contain;
      flex-shrink: 0;
      background: #fff;
      border-radius: 0.4rem;
      padding: 0.2rem;
    }

    &__name {
      font-weight: 600;
      font-size: 1.05rem;
      flex: 1;
      color: $headings-color;
    }

    &__check {
      color: $primary;
    }

    &__hint {
      font-size: 0.82rem;
      color: $text-muted;
    }

    &--active {
      border-color: $primary;
      border-width: 2px;
      padding: calc(1rem - 1px) calc(1.25rem - 1px);
      background: rgba($primary, 0.04);
      box-shadow: 0 4px 15px 0 rgba($primary, 0.1);

      .cbr-provider-card__name {
        color: $primary;
      }
    }

    &--invalid {
      border-color: $danger;
    }

    .dark-layout & {
      background: $theme-dark-card-bg;
      border-color: $theme-dark-border-color;

      .cbr-provider-card__name {
        color: $theme-dark-headings-color;
      }
    }

    .dark-layout &:hover {
      border-color: rgba($primary, 0.6);
      background: rgba($primary, 0.05);
      box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.2);
    }

    .dark-layout &.cbr-provider-card--active {
      border-color: $primary;
      background: rgba($primary, 0.15);
      box-shadow: 0 4px 15px 0 rgba($primary, 0.2);

      .cbr-provider-card__name {
        color: $primary;
      }
    }

    .dark-layout &.cbr-provider-card--invalid {
      border-color: $danger;
    }
  }

  .cbr-barcode-input {
    font-family: $font-family-monospace;
    letter-spacing: 0.05em;
    font-size: 1.05rem;
    height: 2.714rem;
  }

  .cbr-feedback {
    margin-top: 0.25rem;
    margin-bottom: 1rem;

    &__hint {
      display: flex;
      align-items: center;
      font-size: 0.8rem;
    }
  }

  .cbr-count {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.75rem;
    padding: 0.75rem 1rem;
    background: rgba($primary, 0.06);
    border: 1px dashed rgba($primary, 0.3);
    border-radius: 0.5rem;
    flex-wrap: wrap;
    gap: 0.5rem;

    &__left {
      display: inline-flex;
      align-items: center;
      gap: 0.45rem;
    }

    &__label {
      color: $text-muted;
      font-size: 0.85rem;
    }

    &__value {
      font-variant-numeric: tabular-nums;
      font-size: 1.05rem;
      color: $primary;
    }

    &__badge {
      font-weight: 500;
    }
  }

  .cbr-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding-top: 1rem;
    border-top: 1px solid $border-color;
    margin-top: 0.5rem;

    .dark-layout & {
      border-top-color: $theme-dark-border-color;
    }
  }

  .cbr-review {
    &__back {
      background: transparent;
      border: none;
      color: $text-muted;
      font-size: 0.82rem;
      padding: 0;
      cursor: pointer;
      margin-bottom: 0.75rem;
      display: inline-flex;
      align-items: center;
      transition: color 0.15s ease;

      &:hover {
        color: $primary;
      }
    }

    &__title {
      font-weight: 600;
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
    }

    &__card {
      background: rgba(0, 0, 0, 0.02);
      border: 1px solid $border-color;
      border-radius: 0.6rem;
      padding: 0 1.25rem;
      margin-bottom: 1rem;

      .dark-layout & {
        background: rgba(255, 255, 255, 0.03);
        border-color: $theme-dark-border-color;
      }
    }

    &__row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 0.85rem 0;
      border-bottom: 1px solid $border-color;
      gap: 1rem;

      .dark-layout & {
        border-bottom-color: $theme-dark-border-color;
      }
    }

    &__label {
      text-transform: uppercase;
      font-size: 0.7rem;
      letter-spacing: 0.05em;
      color: $text-muted;
      font-weight: 600;
      flex-shrink: 0;
    }

    &__value {
      text-align: right;
      font-weight: 500;

      &--mono {
        font-family: $font-family-monospace;
        font-size: 0.88rem;
      }
    }

    &__total {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem 0;

      &-label {
        text-transform: uppercase;
        font-size: 0.75rem;
        letter-spacing: 0.05em;
        color: $text-muted;
        font-weight: 600;
      }

      &-value {
        font-size: 1.5rem;
        font-variant-numeric: tabular-nums;
        color: $primary;
      }

      &-unit {
        font-size: 0.85rem;
        font-weight: 500;
        color: $text-muted;
        margin-left: 0.35rem;
      }
    }

    &__warning {
      display: flex;
      align-items: center;
      padding: 0.65rem 0.9rem;
      background: rgba($warning, 0.1);
      border: 1px solid rgba($warning, 0.3);
      border-radius: 0.4rem;
      color: darken($warning, 15%);
      font-size: 0.82rem;
      margin-bottom: 1rem;

      .dark-layout & {
        color: lighten($warning, 10%);
      }
    }
  }
}
</style>
